import type { Amount } from '@orus.eu/amount'
import { defineStandaloneForm, type StandaloneFormAnswers } from '@orus.eu/standalone-form'
import { localizable } from '@orus.eu/translations'

export type NlAovqlKnowledgeLabelParameters = Readonly<{
  /** Undefined until the customer declares an income in the funnel. */
  declaredIncomeAmount: Amount | undefined
}>

/**
 * The attempts a customer gets at the knowledge test. The form is declined when the last one fails. The backend reads it
 * on every fold and never stores it, so a change applies to past attempts too.
 */
export const NL_AOVQL_KNOWLEDGE_FORM_MAX_ATTEMPTS = 3

/** A literal, unlike the definition's id, so that records keyed by form id can be typed. */
export const NL_AOVQL_KNOWLEDGE_FORM_ID = 'nl-aovql-knowledge'

/** The knowledge test. Its answer key stays in the backend, since this definition ships to the browser. */
export const nlAovqlKnowledgeForm = defineStandaloneForm<NlAovqlKnowledgeLabelParameters>({
  id: NL_AOVQL_KNOWLEDGE_FORM_ID,
  version: '1.0.0',
  sensitive: false,
})
  .question('priorDisabilityInsurance', {
    kind: 'single-choice',
    options: [
      {
        id: 'yes',
        label: 'nl_aovql_knowledge_test_prior_disability_insurance_yes_option',
      },
      {
        id: 'no',
        label: 'nl_aovql_knowledge_test_prior_disability_insurance_no_option',
      },
    ],
  })
  .question('applicationProcess', {
    kind: 'single-choice',
    options: [
      {
        id: 'insurance-experience',
        label: 'nl_aovql_knowledge_test_application_process_insurance_experience_option',
      },
      {
        id: 'unsure-of-choices',
        label: 'nl_aovql_knowledge_test_application_process_unsure_of_choices_option',
      },
      {
        id: 'little-insurance-knowledge',
        label: 'nl_aovql_knowledge_test_application_process_little_insurance_knowledge_option',
      },
      {
        id: 'researched-insurance',
        label: 'nl_aovql_knowledge_test_application_process_researched_insurance_option',
      },
    ],
  })
  .question('selfServiceDisadvantages', {
    kind: 'single-choice',
    options: [
      {
        id: 'none',
        label: 'nl_aovql_knowledge_test_self_service_disadvantages_none_option',
      },
      {
        id: 'poor-choices',
        label: 'nl_aovql_knowledge_test_self_service_disadvantages_poor_choices_option',
      },
      {
        id: 'more-expensive',
        label: 'nl_aovql_knowledge_test_self_service_disadvantages_more_expensive_option',
      },
    ],
  })
  .question('declaredIncome', {
    kind: 'single-choice',
    options: [
      {
        id: 'needed-if-unable-to-work',
        label: 'nl_aovql_knowledge_test_declared_income_needed_if_unable_to_work_option',
      },
      {
        id: 'before-expenses',
        label: 'nl_aovql_knowledge_test_declared_income_before_expenses_option',
      },
      {
        id: 'taxable-income',
        label: 'nl_aovql_knowledge_test_declared_income_taxable_income_option',
      },
      {
        id: 'hoped-future-income',
        label: 'nl_aovql_knowledge_test_declared_income_hoped_future_income_option',
      },
    ],
  })
  .question('monthlyInsuredAmount', {
    kind: 'single-choice',
    options: [
      {
        id: 'maximum',
        label: 'nl_aovql_knowledge_test_monthly_insured_amount_maximum_option',
      },
      {
        id: 'analyzed-finances',
        label: 'nl_aovql_knowledge_test_monthly_insured_amount_analyzed_finances_option',
      },
      {
        id: 'random',
        label: 'nl_aovql_knowledge_test_monthly_insured_amount_random_option',
      },
    ],
  })
  .question('benefitDuration', {
    kind: 'single-choice',
    options: [
      {
        id: 'long-term-finances',
        label: 'nl_aovql_knowledge_test_benefit_duration_long_term_finances_option',
      },
      {
        id: 'mainly-premium',
        label: 'nl_aovql_knowledge_test_benefit_duration_mainly_premium_option',
      },
      {
        id: 'covered-causes',
        label: 'nl_aovql_knowledge_test_benefit_duration_covered_causes_option',
      },
    ],
  })
  .question('disabilityThreshold', {
    kind: 'single-choice',
    options: [
      {
        id: 'no',
        label: 'nl_aovql_knowledge_test_disability_threshold_no_option',
      },
      {
        id: 'twenty-percent',
        label: 'nl_aovql_knowledge_test_disability_threshold_twenty_percent_option',
      },
      {
        id: 'twenty-five-percent',
        label: 'nl_aovql_knowledge_test_disability_threshold_twenty_five_percent_option',
      },
    ],
  })
  .question('waitingPeriod', {
    kind: 'single-choice',
    options: [
      {
        id: 'ten-months',
        label: 'nl_aovql_knowledge_test_waiting_period_ten_months_option',
      },
      {
        id: 'four-months',
        label: 'nl_aovql_knowledge_test_waiting_period_four_months_option',
      },
      {
        id: 'six-months',
        label: 'nl_aovql_knowledge_test_waiting_period_six_months_option',
      },
    ],
  })
  .question('professionChange', {
    kind: 'single-choice',
    options: [
      {
        id: 'nothing-to-do',
        label: 'nl_aovql_knowledge_test_profession_change_nothing_to_do_option',
      },
      {
        id: 'report-if-income-changes',
        label: 'nl_aovql_knowledge_test_profession_change_report_if_income_changes_option',
      },
      {
        id: 'always-declare',
        label: 'nl_aovql_knowledge_test_profession_change_always_declare_option',
      },
    ],
  })
  .screens([
    {
      id: 'prior-disability-insurance',
      title: 'nl_aovql_knowledge_test_prior_disability_insurance_title',
      tooltip: 'nl_aovql_knowledge_test_prior_disability_insurance_tooltip',
      questions: ['priorDisabilityInsurance'],
    },
    {
      id: 'application-process',
      title: 'nl_aovql_knowledge_test_application_process_title',
      questions: ['applicationProcess'],
    },
    {
      id: 'self-service-disadvantages',
      title: 'nl_aovql_knowledge_test_self_service_disadvantages_title',
      questions: ['selfServiceDisadvantages'],
    },
    {
      id: 'declared-income',
      title: ({ declaredIncomeAmount }) =>
        declaredIncomeAmount === undefined
          ? 'nl_aovql_knowledge_test_declared_income_without_amount_title'
          : localizable({ key: 'nl_aovql_knowledge_test_declared_income_title', parameters: { declaredIncomeAmount } }),
      questions: ['declaredIncome'],
    },
    {
      id: 'monthly-insured-amount',
      title: 'nl_aovql_knowledge_test_monthly_insured_amount_title',
      questions: ['monthlyInsuredAmount'],
    },
    {
      id: 'benefit-duration',
      title: 'nl_aovql_knowledge_test_benefit_duration_title',
      questions: ['benefitDuration'],
    },
    {
      id: 'disability-threshold',
      title: 'nl_aovql_knowledge_test_disability_threshold_title',
      questions: ['disabilityThreshold'],
    },
    {
      id: 'waiting-period',
      title: 'nl_aovql_knowledge_test_waiting_period_title',
      questions: ['waitingPeriod'],
    },
    {
      id: 'profession-change',
      title: 'nl_aovql_knowledge_test_profession_change_title',
      questions: ['professionChange'],
    },
  ])

export type NlAovqlKnowledgeForm = typeof nlAovqlKnowledgeForm
export type NlAovqlKnowledgeFormAnswers = StandaloneFormAnswers<NlAovqlKnowledgeForm>
